// SNIGJ - Clemency Page (President Only)

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { clemencyAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Scale, 
  Search, 
  Filter,
  Download,
  Users,
  Calendar,
  FileText,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const ClemencyPage = () => {
  const { language, user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    min_age: '',
    max_age: '',
    gender: '',
    prison_id: '',
    min_sentence_served_percent: ''
  });

  // Check if user is President
  if (user?.role !== 'PRESIDENT') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <Scale className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="font-heading text-xl font-bold mb-2">
                {language === 'fr' ? 'Accès Restreint' : 'Access Restricted'}
              </h2>
              <p className="text-muted-foreground">
                {language === 'fr' 
                  ? 'Ce module est réservé exclusivement au Président de la République.'
                  : 'This module is exclusively reserved for the President of the Republic.'}
              </p>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  const handleFilter = async () => {
    setLoading(true);
    try {
      const filterData = {};
      if (filters.min_age) filterData.min_age = parseInt(filters.min_age);
      if (filters.max_age) filterData.max_age = parseInt(filters.max_age);
      if (filters.gender) filterData.gender = filters.gender;
      if (filters.prison_id) filterData.prison_id = filters.prison_id;
      if (filters.min_sentence_served_percent) {
        filterData.min_sentence_served_percent = parseFloat(filters.min_sentence_served_percent);
      }

      const response = await clemencyAPI.filterCandidates(filterData);
      setCandidates(response.data.candidates);
      setTotalCount(response.data.count);
      toast.success(language === 'fr' 
        ? `${response.data.count} candidats trouvés` 
        : `${response.data.count} candidates found`);
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de filtrage' : 'Filter error'));
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    // Create CSV export
    const headers = ['UNJI', 'Nom', 'Prénom', 'Prison', 'Statut', 'Date admission'];
    const csvContent = [
      headers.join(','),
      ...candidates.map(c => [
        c.unji,
        c.last_name,
        c.first_name,
        c.prison_id,
        c.status,
        c.admission_date
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clemency_candidates_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success(language === 'fr' ? 'Liste exportée' : 'List exported');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="clemency-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded bg-yellow-400 flex items-center justify-center">
                <Scale className="w-7 h-7 text-slate-900" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">
                  {t(language, 'clemency.title')}
                </h1>
                <p className="text-muted-foreground">
                  {language === 'fr' ? 'Présidence de la République' : 'Presidency of the Republic'}
                </p>
              </div>
            </div>
          </div>
          {candidates.length > 0 && (
            <Button onClick={handleExport} className="gap-2" data-testid="export-clemency-btn">
              <Download className="w-4 h-4" />
              {t(language, 'clemency.exportList')}
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">
              {t(language, 'clemency.filterCandidates')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>{t(language, 'clemency.minAge')}</Label>
                <Input
                  type="number"
                  min="0"
                  value={filters.min_age}
                  onChange={(e) => setFilters({ ...filters, min_age: e.target.value })}
                  placeholder="18"
                  data-testid="clemency-min-age"
                />
              </div>
              <div className="space-y-2">
                <Label>{t(language, 'clemency.maxAge')}</Label>
                <Input
                  type="number"
                  min="0"
                  value={filters.max_age}
                  onChange={(e) => setFilters({ ...filters, max_age: e.target.value })}
                  placeholder="70"
                  data-testid="clemency-max-age"
                />
              </div>
              <div className="space-y-2">
                <Label>{t(language, 'prisoners.gender')}</Label>
                <Select
                  value={filters.gender || "ALL"}
                  onValueChange={(value) => setFilters({ ...filters, gender: value === "ALL" ? "" : value })}
                >
                  <SelectTrigger data-testid="clemency-gender">
                    <SelectValue placeholder={language === 'fr' ? 'Tous' : 'All'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    <SelectItem value="M">{t(language, 'prisoners.male')}</SelectItem>
                    <SelectItem value="F">{t(language, 'prisoners.female')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t(language, 'clemency.minSentenceServed')}</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={filters.min_sentence_served_percent}
                  onChange={(e) => setFilters({ ...filters, min_sentence_served_percent: e.target.value })}
                  placeholder="50"
                  data-testid="clemency-sentence-percent"
                />
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  onClick={handleFilter} 
                  className="w-full gap-2"
                  disabled={loading}
                  data-testid="clemency-filter-btn"
                >
                  <Filter className="w-4 h-4" />
                  {t(language, 'clemency.generateList')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Summary */}
        {totalCount > 0 && (
          <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 rounded bg-yellow-100 dark:bg-yellow-900/50 flex items-center justify-center">
                <Users className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold font-heading">{totalCount}</p>
                <p className="text-sm text-muted-foreground">
                  {t(language, 'clemency.candidates')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Candidates Table */}
        {candidates.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                {t(language, 'clemency.candidates')}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr className="bg-muted/50">
                      <th>{t(language, 'prisoners.unji')}</th>
                      <th>{language === 'fr' ? 'Nom complet' : 'Full Name'}</th>
                      <th>{t(language, 'prisoners.gender')}</th>
                      <th>{t(language, 'prisoners.prison')}</th>
                      <th>{t(language, 'prisoners.status')}</th>
                      <th>{t(language, 'prisoners.admissionDate')}</th>
                      <th>{t(language, 'prisoners.sentenceEnd')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((candidate) => (
                      <tr key={candidate.prisoner_id} data-testid={`clemency-row-${candidate.prisoner_id}`}>
                        <td className="font-mono text-sm">{candidate.unji}</td>
                        <td>{candidate.first_name} {candidate.last_name}</td>
                        <td>{candidate.gender === 'M' ? t(language, 'prisoners.male') : t(language, 'prisoners.female')}</td>
                        <td>{candidate.prison_id}</td>
                        <td>
                          <span className="px-2 py-1 rounded text-xs bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                            {t(language, `prisoners.${candidate.status.toLowerCase()}`)}
                          </span>
                        </td>
                        <td>
                          <span className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3" />
                            {candidate.admission_date}
                          </span>
                        </td>
                        <td>
                          <span className="flex items-center gap-1 text-sm">
                            <Calendar className="w-3 h-3" />
                            {candidate.sentence_end || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!loading && candidates.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-lg font-bold mb-2">
                {language === 'fr' ? 'Aucun candidat filtré' : 'No filtered candidates'}
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                {language === 'fr'
                  ? 'Utilisez les filtres ci-dessus pour générer une liste de candidats éligibles à la grâce présidentielle.'
                  : 'Use the filters above to generate a list of candidates eligible for presidential clemency.'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ClemencyPage;
